// Vendor Program v2 PR 2 — shared status vocab for monthly vendor reports
// and the complaints queue. Same condition, same color everywhere (the
// vendorBillingFormat convention).

import type { StatusTone } from "@/design-system/components/ui";
import type {
  VendorComplaintStatus,
  VendorReportStatus,
} from "@/api/portfolioAdmin/vendorReportsApi";

export const VENDOR_REPORT_STATUS_LABEL: Record<VendorReportStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  returned: "Returned",
  accepted: "Accepted",
  "signed-off": "Signed off",
  void: "Void",
};

/** draft → submitted → returned → accepted → signed-off ladder. */
export function vendorReportStatusTone(status: VendorReportStatus): StatusTone {
  switch (status) {
    case "submitted":
      return "info";
    case "returned":
      return "warn";
    case "accepted":
      return "progress";
    case "signed-off":
      return "ok";
    case "void":
      return "muted";
    default:
      return "neutral";
  }
}

/**
 * The expected-vs-filed badge, in THREE states rather than two.
 *
 * The badge used to branch on `outstanding.length` alone, so "nothing is
 * owed" and "everything owed is filed" were indistinguishable: a loan with
 * no vendor assigned at all showed a green "All reports filed" directly
 * above body text reading "No monthly vendor reports on this loan yet".
 * `VendorReportComplianceService` short-circuits to (0, 0, []) when there
 * are no active vendor assignments, and `expectedCount` — already on the
 * DTO — is what tells the two apart.
 *
 * Shared by the loan Vendors tab and the vendor registry's Reports tab:
 * same data, same question, so they must not answer it differently.
 */
export function vendorReportComplianceBadge(compliance: {
  expectedCount: number;
  filedCount: number;
  outstanding: readonly unknown[];
}): { tone: StatusTone; label: string; hint: string } {
  const outstanding = compliance.outstanding.length;
  if (compliance.expectedCount === 0) {
    return {
      tone: "neutral",
      label: "No vendor reports required",
      hint: "No active vendor assignment is expected to file for this period.",
    };
  }
  const hint = `${compliance.filedCount} of ${compliance.expectedCount} expected vendor reports filed.`;
  return outstanding > 0
    ? { tone: "warn", label: `${outstanding} outstanding`, hint }
    : { tone: "ok", label: "All reports filed", hint };
}

export const COMPLAINT_STATUS_LABEL: Record<VendorComplaintStatus, string> = {
  new: "New",
  acknowledged: "Acknowledged",
  resolved: "Resolved",
  "handed-off": "Handed off",
};

/** new=info, acknowledged=progress, resolved=ok, handed-off=neutral. */
export function complaintStatusTone(status: VendorComplaintStatus): StatusTone {
  switch (status) {
    case "new":
      return "info";
    case "acknowledged":
      return "progress";
    case "resolved":
      return "ok";
    default:
      return "neutral";
  }
}
